import cx from 'classnames';
import {Fragment, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Loader} from 'components';
import {TRANSLATION_KEYS} from 'locale/translation-keys';

type Props = {
    isLoading?: boolean;
    noData?: boolean;
    isError?: boolean;
    errorPlaceholder?: string;
    noDataPlaceholder?: string;
    errorClassName?: string;
    noDataClassName?: string;
    hideContentOnLoading?: boolean;
    svgClassName?: string;
    loadingMinHeight?: boolean;
    loaderAbsolute?: boolean;
    children?: React.ReactNode;
};

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.DATA_WRAPPER;
const T_KEYS_COMMON = TRANSLATION_KEYS.COMMON;

enum DataStatus {
    Loading = 'loading',
    Error = 'error',
    Empty = 'empty',
    OK = 'ok'
}

export const DataWrapper = ({
    isLoading, noData, noDataClassName = 'tw-h-16', hideContentOnLoading = true, loadingMinHeight = true,
    svgClassName = 'tw-max-h-12', isError = false, errorPlaceholder, loaderAbsolute = true, noDataPlaceholder, errorClassName, children
}: Props) => {
    const {t} = useTranslation();

    const status = useMemo(() => {
        if (isLoading) {
            return DataStatus.Loading;
        } else if (isError) {
            return DataStatus.Error;
        } else if (noData) {
            return DataStatus.Empty;
        } else {
            return DataStatus.OK;
        }
    }, [isLoading, isError, noData]);

    const showContent = useMemo(() =>
        status === DataStatus.OK || status === DataStatus.Loading && !hideContentOnLoading,
    [status, hideContentOnLoading]);

    return (
        <Fragment>
            {status === DataStatus.Error &&
                <div className={cx('tw-flex-center tw-mx-auto tw-ts-h5 md:tw-ts-h2 tw-text-alert tw-text-opacity-40', errorClassName)}>
                    {errorPlaceholder || t(T_KEYS_COMMON.MESSAGES.ERROR.UNEXPECTED)}
                </div>}
            {status === DataStatus.Empty &&
                <div className={cx('tw-flex-center tw-mx-auto tw-ts-h5 md:tw-ts-h2 tw-text-default tw-text-opacity-40', noDataClassName)}>
                    {noDataPlaceholder || t(T_KEYS.NO_DATA_PLACEHOLDER)}
                </div>}
            {status === DataStatus.Loading &&
                <div className={cx('tw-inset-0 tw-z-loader-wrapper tw-flex-center',
                    {
                        'tw-bg-light tw-bg-opacity-50 tw-absolute tw-top-0': !hideContentOnLoading,
                        'tw-min-h-16': loadingMinHeight
                    }
                )}
                >
                    <Loader svgClassName={svgClassName} absolute={loaderAbsolute} />
                </div>}
            {showContent && children}
        </Fragment>
    );
};
